import { Volume2, Pause, Play, Loader2 } from "lucide-react";
import { speechSupported, toggle, stop, useAudioReaderState } from "@/lib/audioReader";

// Full pill button with progress bar — e.g. "Listen to this study".
export function ListenButton({ id, getText, label = "Listen", testid = "listen-study" }) {
  const { activeId, status, progress } = useAudioReaderState();
  if (!speechSupported) return null;
  const active = activeId === id;
  const loading = active && status === "loading";
  const playing = active && status === "playing";
  const paused = active && status === "paused";

  const onClick = () => {
    const text = typeof getText === "function" ? getText() : getText;
    toggle(id, text);
  };

  const labelText = loading ? "Starting…" : playing ? "Pause" : paused ? "Resume" : label;

  return (
    <div className="inline-flex flex-col gap-1.5" data-testid={`${testid}-wrap`}>
      <div className="inline-flex items-center gap-2">
        <button
          data-testid={testid}
          onClick={onClick}
          className="inline-flex items-center gap-2 rounded-full bg-[#10222A] hover:bg-[#10222A] text-[#00B8CC] border border-[#00E5FF]/20 pl-3.5 pr-4 py-2 text-sm font-medium transition-colors active:scale-95"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.75} />
          ) : playing ? (
            <Pause className="w-4 h-4" strokeWidth={1.75} />
          ) : paused ? (
            <Play className="w-4 h-4" strokeWidth={1.75} />
          ) : (
            <Volume2 className="w-4 h-4" strokeWidth={1.75} />
          )}
          <span>{labelText}</span>
        </button>
        {active && (
          <button
            data-testid={`${testid}-stop`}
            onClick={() => stop()}
            className="text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            Stop
          </button>
        )}
      </div>
      {active && !loading && (
        <div className="h-1 w-full max-w-[220px] rounded-full bg-[#1E273D] overflow-hidden" data-testid={`${testid}-progress`}>
          <div
            className="h-full bg-[#00E5FF] transition-[width] duration-300 ease-linear"
            style={{ width: `${Math.round((progress || 0) * 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}

// Small round icon button for individual sections / chat answers.
export function ListenIconButton({ id, getText, testid, title = "Listen" }) {
  const { activeId, status } = useAudioReaderState();
  if (!speechSupported) return null;
  const active = activeId === id;
  const loading = active && status === "loading";
  const playing = active && status === "playing";
  const paused = active && status === "paused";

  const onClick = (e) => {
    e.stopPropagation();
    const text = typeof getText === "function" ? getText() : getText;
    toggle(id, text);
  };

  return (
    <button
      data-testid={testid}
      onClick={onClick}
      title={playing ? "Pause" : paused ? "Resume" : title}
      aria-label={playing ? "Pause" : paused ? "Resume" : title}
      className={`inline-flex items-center justify-center rounded-full w-7 h-7 transition-colors active:scale-90 ${
        active
          ? "bg-[#00E5FF] text-[#07090E]"
          : "text-[#00E5FF] hover:text-[#00B8CC] hover:bg-[#10222A]"
      }`}
    >
      {loading ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />
      ) : playing ? (
        <Pause className="w-3.5 h-3.5" strokeWidth={2} />
      ) : paused ? (
        <Play className="w-3.5 h-3.5" strokeWidth={2} />
      ) : (
        <Volume2 className="w-3.5 h-3.5" strokeWidth={2} />
      )}
    </button>
  );
}
